import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'

import {
  ALL_LANGUAGES_QUERY,
  SINGLE_LANGUAGE_QUERY,
  CREATE_LANGUAGE_MUTATION,
  UPDATE_LANGUAGE_MUTATION,
  DELETE_LANGUAGE_MUTATION,
  FORCE_DELETE_LANGUAGE_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const languageRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_LANGUAGE_QUERY, variables).then((data: any) => {
      return hydrateObject(data.language)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_LANGUAGES_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.languages)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_LANGUAGE_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.language),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_LANGUAGES_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.languages),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_LANGUAGE_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.language)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_LANGUAGE_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.language)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_LANGUAGE_MUTATION, variables).then((data: any) => {
      return data.deleteLanguage
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_LANGUAGE_MUTATION, variables).then((data: any) => {
      return data.forceDeleteLanguage
    })
  },
}

export default languageRepository
